import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const StoreStatusGrid = ({ stores, onStoreClick }) => {
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const navigate = useNavigate();

  const statusOptions = [
    { value: 'all', label: 'All Stores' },
    { value: 'active', label: 'Active' },
    { value: 'pending', label: 'Pending Updates' },
    { value: 'expired', label: 'Expired Items' },
    { value: 'missing', label: 'Missing Layouts' }
  ];

  const sortOptions = [
    { value: 'name', label: 'Store Name' },
    { value: 'cost', label: 'Total Cost' },
    { value: 'items', label: 'Branding Items' },
    { value: 'updated', label: 'Last Updated' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success bg-success/10';
      case 'pending': return 'text-warning bg-warning/10';
      case 'expired': return 'text-error bg-error/10';
      case 'missing': return 'text-muted-foreground bg-muted/10';
      default: return 'text-muted-foreground bg-muted/10';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'CheckCircle';
      case 'pending': return 'Clock';
      case 'expired': return 'AlertTriangle';
      case 'missing': return 'FileX';
      default: return 'Store';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(value);
  };

  const filteredStores = stores?.filter(store => {
    if (filterStatus === 'all') return true;
    return store?.status === filterStatus;
  });

  const sortedStores = [...filteredStores]?.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a?.name?.localeCompare(b?.name);
      case 'cost':
        return b?.totalCost - a?.totalCost;
      case 'items':
        return b?.brandingItems - a?.brandingItems;
      case 'updated':
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      default:
        return 0;
    }
  });

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Store Status Overview</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/store-management')}
        >
          <Icon name="Settings" size={16} />
          Manage Stores
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <Select
          options={statusOptions}
          value={filterStatus}
          onChange={setFilterStatus}
          className="sm:w-48"
        />
        <Select
          options={sortOptions}
          value={sortBy}
          onChange={setSortBy}
          className="sm:w-48"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sortedStores?.map((store) => (
          <div
            key={store?.id}
            className="border border-border rounded-lg p-4 hover:shadow-elevated transition-all duration-200 cursor-pointer group"
            onClick={() => onStoreClick(store)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Store" size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors duration-200">
                    {store?.name}
                  </h4>
                  <p className="text-xs text-muted-foreground">{store?.location}</p>
                </div>
              </div>
              
              <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(store?.status)}`}>
                <Icon name={getStatusIcon(store?.status)} size={12} />
                {store?.status}
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Total Cost</span>
                <span className="text-sm font-medium text-foreground">
                  {formatCurrency(store?.totalCost)}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Branding Items</span>
                <span className="text-sm font-medium text-foreground">
                  {store?.brandingItems}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Size</span>
                <span className="text-sm font-medium text-foreground">
                  {store?.size} sq ft
                </span>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t border-border">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  Updated {new Date(store.lastUpdated)?.toLocaleDateString('en-GB')}
                </span>
                <Icon name="ChevronRight" size={16} className="text-muted-foreground group-hover:text-primary transition-colors duration-200" />
              </div>
            </div>
          </div>
        ))}
      </div>
      {sortedStores?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Store" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No stores found matching the selected criteria</p>
        </div>
      )}
    </div>
  );
};

export default StoreStatusGrid;